import type { BlockLike } from '@maestro/commerce-blocks/components/metadata/metadata.types'
import type { Review } from '@maestro/data-layer'
import type { SanityCmsContent } from '@maestro/sanity/types'

// Recursively omit empty, undefined, null, or 'undefined'-containing string values
export function omitEmpty<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          (typeof value !== 'string' || !value.includes('undefined'))
      )
      .map(([key, value]) => [
        key,
        typeof value === 'object' && !Array.isArray(value) && value !== null
          ? omitEmpty(value)
          : value,
      ])
  ) as T
}

export function removeTrailingDot(text?: string): string | undefined {
  if (!text) {
    return undefined
  }
  return text.endsWith('.') ? text.slice(0, -1) : text
}

export function getReviewsMetadata(reviews: Review[]) {
  if (!reviews.length) {
    return []
  }

  return reviews.map((review) => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.date,
    reviewBody: review.text,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
  }))
}

function collectTextNodes(value: SanityCmsContent, texts: string[]): void {
  if (!value) {
    return
  }

  if (typeof value === 'string') {
    texts.push(value)
    return
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectTextNodes(entry, texts))
    return
  }

  if (typeof value !== 'object') {
    return
  }

  const node = value as BlockLike

  if (typeof node.text === 'string') {
    texts.push(node.text)
  }

  if (node.children) {
    collectTextNodes(node.children, texts)
  }
}

export function extractTextFromBlocks(value: SanityCmsContent): string {
  const texts: string[] = []
  collectTextNodes(value, texts)

  return texts
    .map((text) => text.trim())
    .filter(Boolean)
    .join(' ')
}

// Joins base and path, ensuring no double slashes except after protocol
export function normalizeUrl(base: string, path: string): string {
  if (base?.length && !base.endsWith('/')) {
    base += '/'
  }
  if (path?.length && path.startsWith('/')) {
    path = path.slice(1)
  }
  return base + path
}

export function stripHtmlTags(text: string): string {
  return text.replace(/<[^>]*>/g, '')
}
